import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ALERT_COOLDOWN_MS,
  EVENT_INTERVAL_MS,
  NODE_ACTIVITY_MAX,
  TICKER_MAX
} from './constants'
import { MOCK_EVENT_TEMPLATES, type EventTemplate } from './mockData'
import type {
  EventKind,
  GraphEdge,
  GraphKpis,
  GraphNode,
  LiveEvent,
  NodeKind
} from './types'

let counter = 0
const nextId = () => `cg-ev-${counter++}`

/** Generic event templates synthesized for real nodes (app/user/company). */
const GENERIC_EVENTS: Partial<
  Record<NodeKind, Omit<EventTemplate, 'nodeId'>[]>
> = {
  company: [{ kind: 'action', textKey: 'memberInvited' }],
  app: [
    { kind: 'ok', textKey: 'appDeploy' },
    { kind: 'info', textKey: 'appSession' }
  ],
  user: [
    { kind: 'ok', textKey: 'userSignin' },
    { kind: 'action', textKey: 'userToken' }
  ]
}

/** Classify a cross-node edge into an interaction event (source acts on target). */
const interactionFor = (
  source: GraphNode,
  target: GraphNode
): { kind: EventKind; textKey: string } => {
  if (source.kind === 'agent' && target.kind === 'app') {
    return { kind: 'action', textKey: 'agentCalledApp' }
  }
  if (source.kind === 'agent' && target.kind === 'database') {
    return { kind: 'ok', textKey: 'agentSyncedData' }
  }
  if (source.kind === 'app' && target.kind === 'database') {
    return { kind: 'info', textKey: 'appQueriedDb' }
  }
  if (source.kind === 'app' && target.kind === 'email') {
    return { kind: 'info', textKey: 'appSentEmail' }
  }

  return { kind: 'info', textKey: 'sentData' }
}

const buildPool = (nodes: GraphNode[], edges: GraphEdge[]): EventTemplate[] => {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const ids = new Set(nodes.map((n) => n.id))
  const pool: EventTemplate[] = MOCK_EVENT_TEMPLATES.filter((tpl) =>
    ids.has(tpl.nodeId)
  )

  nodes.forEach((n) => {
    const generic = GENERIC_EVENTS[n.kind]
    if (!generic) return
    generic.forEach((g) =>
      pool.push({
        nodeId: n.id,
        // real nodes connect to company via `company->${id}`
        edgeId: n.id === 'company' ? undefined : `company->${n.id}`,
        kind: g.kind,
        textKey: g.textKey
      })
    )
  })

  // Inter-node interactions: source node "acts on" target node, particle flows.
  edges.forEach((e) => {
    const sid = typeof e.source === 'string' ? e.source : e.source.id
    const tid = typeof e.target === 'string' ? e.target : e.target.id
    if (sid === 'company' || tid === 'company') return
    const source = byId.get(sid)
    const target = byId.get(tid)
    if (!source || !target) return
    const { kind, textKey } = interactionFor(source, target)
    pool.push({
      nodeId: sid,
      edgeId: e.id,
      kind,
      textKey,
      params: { target: target.label }
    })
  })

  return pool
}

type Result = {
  feed: LiveEvent[]
  nodeActivity: Record<string, LiveEvent[]>
  edgeActivity: Record<string, LiveEvent[]>
  alerts: Record<string, EventKind | undefined>
  kpis: GraphKpis
  edgePing: { edgeId: string; at: number } | null
}

/**
 * Simulated real-time event engine. This is the single seam: replace the
 * interval with a WebSocket / polled audit stream and every consumer (ticker,
 * particles, alert rings, KPI counters) keeps working unchanged.
 *
 * When `paused` (tab hidden or reduced-motion), no new events are emitted.
 */
export const useLiveEvents = (
  nodes: GraphNode[],
  edges: GraphEdge[],
  paused: boolean,
  agentsRunning: number
): Result => {
  const { t } = useTranslation()
  const [feed, setFeed] = useState<LiveEvent[]>([])
  const [nodeActivity, setNodeActivity] = useState<Record<string, LiveEvent[]>>(
    {}
  )
  const [edgeActivity, setEdgeActivity] = useState<Record<string, LiveEvent[]>>(
    {}
  )
  const [alerts, setAlerts] = useState<Record<string, EventKind | undefined>>(
    {}
  )
  // Only the interval-driven counters are stored; agentsRunning + openAlerts are
  // derived in the returned `kpis` so we never sync state from state in an effect.
  const [counters, setCounters] = useState({
    activeSessions: 312,
    eventsPerMin: 18
  })
  const [edgePing, setEdgePing] = useState<Result['edgePing']>(null)

  const pool = useMemo(() => buildPool(nodes, edges), [nodes, edges])

  useEffect(() => {
    if (paused || pool.length === 0) return undefined

    const tick = () => {
      const tpl = pool[counter % pool.length]
      const ev: LiveEvent = {
        id: nextId(),
        nodeId: tpl.nodeId,
        edgeId: tpl.edgeId,
        kind: tpl.kind,
        text: t(
          `workspaceOverview.companyGraph.events.${tpl.textKey}`,
          tpl.params
        ),
        ts: new Date().toISOString()
      }

      setFeed((f) => [ev, ...f].slice(0, TICKER_MAX))
      setNodeActivity((m) => ({
        ...m,
        [ev.nodeId]: [ev, ...(m[ev.nodeId] ?? [])].slice(0, NODE_ACTIVITY_MAX)
      }))
      if (ev.edgeId) {
        const edgeId = ev.edgeId
        setEdgePing({ edgeId, at: Date.now() })
        setEdgeActivity((m) => ({
          ...m,
          [edgeId]: [ev, ...(m[edgeId] ?? [])].slice(0, NODE_ACTIVITY_MAX)
        }))
      }

      if (ev.kind === 'warn' || ev.kind === 'error') {
        setAlerts((a) => ({ ...a, [ev.nodeId]: ev.kind }))
        window.setTimeout(
          () =>
            setAlerts((a) => {
              const next = { ...a }
              delete next[ev.nodeId]

              return next
            }),
          ALERT_COOLDOWN_MS
        )
      }

      setCounters((c) => ({
        activeSessions: Math.max(0, c.activeSessions + ((counter % 7) - 3)),
        eventsPerMin: 12 + (counter % 14)
      }))
    }

    const handle = window.setInterval(tick, EVENT_INTERVAL_MS)

    return () => window.clearInterval(handle)
  }, [paused, t, pool])

  const kpis = useMemo<GraphKpis>(
    () => ({
      activeSessions: counters.activeSessions,
      eventsPerMin: counters.eventsPerMin,
      agentsRunning,
      openAlerts: Object.values(alerts).filter(Boolean).length
    }),
    [counters, agentsRunning, alerts]
  )

  return { feed, nodeActivity, edgeActivity, alerts, kpis, edgePing }
}
